import { createFileRoute } from '@tanstack/react-router';
import BgIcon from '../../../../microcomponents/BgIcon';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Archive02FreeIcons,
  KeyIcon,
  LockKeyIcon, MoreHorizontalIcon,
  User02FreeIcons, UserAdd01FreeIcons
} from '@hugeicons/core-free-icons';
import { Avatar, Collapse, Input, Switch } from 'antd';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, Button } from '@virlewood/board-79';
import { IconArchive, IconDots, IconRefresh } from '@tabler/icons-react';
import * as React from 'react';
import { COLORS } from '@next-is-free-monorepo/globals';
import CModal from '../../../../components/CModal';
import NewStaffMemberModal from '../../../../components/settings/NewStaffMemberModal';
import { useState } from 'react';
import { useMutation, UseMutationOptions, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { staffQueryOptions } from '../../../../utils/queryOptions';
import Loader from '../../../../components/Loader';
import { apiV1 } from '../../../../utils/apiClient';
import SingleCollapse from '../../../../components/SingleCollapse';
import CopyText from '../../../../microcomponents/CopyText';
import SettingsSection from '../../../../components/settings/SettingsSection';
import { NButton } from '@next-is-free-monorepo/nif-web-components';
import PlanGate from '../../../../components/PlanGate';
import usePlanLimits from '../../../../utils/hooks/usePlanLimits';
import { $api } from '../../../../utils/openApiClient';
import PageLoader from '../../../../microcomponents/PageLoader';
import StaffMemberAvatar from '../../../../microcomponents/StaffMemberAvatar';

export const Route = createFileRoute('/_authenticated/settings/staff')({
  component: RouteComponent,
  pendingComponent: props => <PageLoader />
});

function RouteComponent() {

  const [newMemberModalOpen, setNewMemberModalOpen] = useState(false);

  const staff = useSuspenseQuery(staffQueryOptions({ withArchived: true }));
  const { usedPlanLimits } = usePlanLimits();

  const activeMembers = staff.data?.filter((item) => !item.archived) || [];

  return (
    <>
      <SettingsSection title={'Staff'}
                       subtitle={'Qui puoi aggiungere i membri del tuo staff e generare credenziali che danno accesso esclusivamente all\'app business. In questo modo puoi tenere al sicuro le credenziali del tuo account principale.'}>
        <div>
          <div className={'flex items-center gap-2 mt-5 mb-3 justify-between'}>
            <div className={'flex items-center gap-2'}>
              <BgIcon icon={<HugeiconsIcon icon={LockKeyIcon} strokeWidth={2} />} color={'blue'} />
              <p className={'font-medium'}>Staff attivo <span
                className={'text-slate-400'}>{usedPlanLimits(activeMembers.length, 'maxStaffMembers')}</span></p>
            </div>
            <div>
              <PlanGate hideMask featureKey={'maxStaffMembers'}>
                <Button variant={'secondary'} startIcon={'hugeicons:user-add-01'} size={'sm'}
                        onClick={() => setNewMemberModalOpen(true)}>Nuovo</Button>
              </PlanGate>
            </div>
          </div>
          {activeMembers.map((member) => (
            <Row {...member} displayName={member.display_name} key={member.username} />
          ))}
          {!staff.data?.some((item) => !item.archived) &&
            <div className={'flex items-center justify-center mt-5 bg-slate-50 p-2 rounded-md'}>
              <p className={'text-slate-500'}>Nessuno staff attivo</p>
            </div>
          }
          <SingleCollapse className={'mt-5'} header={<div className={'flex items-center gap-2'}>
            <BgIcon icon={<HugeiconsIcon icon={Archive02FreeIcons} strokeWidth={2} />} color={'black'} />
            <p className={'font-medium'}>Staff archiviato</p>
          </div>}>
            <div>
              {staff.data?.map((member) => (
                member.archived &&
                <Row {...member} displayName={member.display_name} key={member.username} />
              ))}
            </div>
          </SingleCollapse>
        </div>
        <NewStaffMemberModal open={newMemberModalOpen} setOpen={setNewMemberModalOpen} />
      </SettingsSection>
    </>
  );
}

function Row({ id, displayName, username, archived }: {
  id: string,
  displayName: string,
  username: string,
  archived: boolean
}) {

  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [newPassword, setNewPassword] = useState<undefined | string>(undefined);
  const queryClient = useQueryClient();

  const refreshPassword = useMutation({
    mutationFn: async () => await apiV1.get<string>(`/shop/staff/${id}/refresh-password`),
    onSuccess(data) {
      setNewPassword(data);
    }
  });

  const archive: UseMutationOptions = {
    mutationFn: async () => await apiV1.put(`/shop/staff/${id}/archive`, {}),
    onSuccess() {
      setConfirmArchiveOpen(false);
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    }
  };

  return (
    <div className={`border p-3 rounded-lg mt-2 flex items-center justify-between ${archived ? 'bg-slate-50' : ''}`}>
      <div className={'items-center flex gap-2'}>
        <StaffMemberAvatar size={'small'} id={id} />
        <div>
          <p className={'font-medium leading-none text-sm'}>{displayName}</p>
          <p className={'text-slate-500 leading-none text-xs'}>{username}</p>
        </div>
      </div>
      {!archived &&
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button iconOnly variant={'outline'} size={'sm'} className={'rounded-full'}
                    startIcon={'hugeicons:more-horizontal'} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => refreshPassword.mutate()}>
              <IconRefresh size={16} />
              Rigenera password
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setConfirmArchiveOpen(true)}>
              <IconArchive size={16} />
              Archivia
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
      <CModal open={confirmArchiveOpen} color={'red'} title={'Archivia utente dello Staff'}
              text={'Archiviando questo utente dello Staff non avrà più accesso all\'App Business'}
              confirmButtonText={'Archivia'}
              onClose={() => setConfirmArchiveOpen(false)} icon={<HugeiconsIcon icon={Archive02FreeIcons} />}
              mutationOptions={archive} />
      <CModal open={!!newPassword} onClose={() => setNewPassword(undefined)} icon={<HugeiconsIcon icon={KeyIcon} />}
              mutationOptions={{}} hideConfirmButton cancelButtonText={'Chiudi'}>
        <CopyText label={'Nuova Password'} text={newPassword || ''} />
      </CModal>
    </div>
  );
}
